import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import authService from '../services/authService';
import { clearAllStoredThreads } from '../lib/assistantThreadStorage';
import { AUTH_ERROR_EVENT, finishPendingLogout, getAccessToken } from '../services/session';

interface User {
  id: string;
  email: string;
  business_name?: string;
  contact_person?: string;
  phone_number?: string;
  timeglobe_auth_key?: string;
  is_active?: boolean;
  created_at?: string;
  whatsapp_number?: string;
  customer_id?: string;
}

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (
    businessName: string, 
    email: string, 
    phoneNumber: string, 
    password: string
  ) => Promise<boolean>;
  verifyOtp: (email: string, otp: string) => Promise<boolean>;
  resendOtp: (email: string) => Promise<boolean>;
  forgotPassword: (email: string) => Promise<boolean>;
  resetPassword: (businessId: string, token: string, newPassword: string) => Promise<boolean>;
  refreshUserData: () => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  /** False until the first session check has finished; `isAuthenticated` is not final before. */
  authReady: boolean;
  /** The session check cannot reach the server yet and keeps trying. */
  serverUnreachable: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper to store user in sessionStorage to persist across page refreshes
const storeUserInSession = (user: User | null) => {
  if (user) {
    sessionStorage.setItem('currentUser', JSON.stringify(user));
  } else {
    sessionStorage.removeItem('currentUser');
  }
};

// Helper to retrieve user from sessionStorage
const getUserFromSession = (): User | null => {
  const userData = sessionStorage.getItem('currentUser');
  return userData ? JSON.parse(userData) : null;
};

/** What the session check found. 'unreachable': the server gave no answer either way. */
type SessionCheck = 'signed-in' | 'signed-out' | 'unreachable';

/** A profile request that hangs would keep a new tab on the check screen forever. */
const PROFILE_TIMEOUT_MS = 15_000;
/** Waits between session checks while the server is unreachable (last one repeats). */
const UNREACHABLE_RETRY_DELAYS_MS = [2000, 4000, 8000, 15000];

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const isSignedOutError = (error: unknown) =>
  error instanceof Error && error.message.includes('Authentication expired');

/** The signed-in account's profile, or a rejection if no answer came in time. */
const loadProfile = async () => {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), PROFILE_TIMEOUT_MS);
  try {
    return await authService.getBusinessProfile(abort.signal);
  } finally {
    clearTimeout(timer);
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(() => getUserFromSession());
  const [loading, setLoading] = useState<boolean>(true);
  const [authReady, setAuthReady] = useState<boolean>(false);
  const [serverUnreachable, setServerUnreachable] = useState<boolean>(false);

  // Update sessionStorage when currentUser changes
  useEffect(() => {
    storeUserInSession(currentUser);
  }, [currentUser]);

  // The session is over for good: the server said so (auth-error), or the
  // user logged out in another tab (its token disappears from localStorage).
  useEffect(() => {
    const handleAuthError = () => {
      setCurrentUser(null);
      window.location.href = '/login';
    };
    const handleStorage = (event: StorageEvent) => {
      const tokenRemoved = event.key === 'token' && !!event.oldValue && !event.newValue;
      // Only a tab that shows someone as signed in has anything to leave.
      if (tokenRemoved && sessionStorage.getItem('currentUser')) handleAuthError();
    };

    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const logout = React.useCallback(async () => {
    setCurrentUser(null);
    sessionStorage.removeItem('currentUser');
    // The assistant's thread holds customer names and numbers: it ends with the login.
    clearAllStoredThreads();
    await authService.logout();
    window.location.href = '/login';
  }, []);

  // Who is signed in, asked of the server
  const checkSession = React.useCallback(async (): Promise<SessionCheck> => {
    let token: string | null;
    try {
      // Renews the login from the session cookie when the access token has
      // run out — after 30 minutes or after days with the browser closed.
      token = await getAccessToken();
    } catch (error) {
      // Keep whatever this tab already knows about the user.
      console.error('Session check could not reach the server:', error);
      return 'unreachable';
    }
    if (!token) {
      setCurrentUser(null);
      return 'signed-out';
    }

    try {
      const userData = await loadProfile();
      const user = {
        id: userData.id || '',
        email: userData.email || '',
        business_name: userData.business_name,
        contact_person: userData.contact_person,
        phone_number: userData.phone_number,
        timeglobe_auth_key: userData.timeglobe_auth_key
      };
      setCurrentUser(user);
      return 'signed-in';
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // A session that ended meanwhile is handled by the auth-error listener.
      return isSignedOutError(error) ? 'signed-out' : 'unreachable';
    }
  }, []);

  // Helper function to fetch and set user data
  const fetchUserData = React.useCallback(
    async (): Promise<boolean> => (await checkSession()) === 'signed-in',
    [checkSession],
  );

  useEffect(() => {
    let cancelled = false;

    // Check if user is already logged in. While the server gives no answer the
    // check repeats: showing the login form to someone who is signed in — and
    // cannot sign in while the server is down — helps nobody.
    const checkAuth = async () => {
      await finishPendingLogout();
      for (let attempt = 0; !cancelled; attempt += 1) {
        const outcome = await checkSession();
        if (cancelled) return;
        if (outcome !== 'unreachable') break;
        setServerUnreachable(true);
        await sleep(UNREACHABLE_RETRY_DELAYS_MS[Math.min(attempt, UNREACHABLE_RETRY_DELAYS_MS.length - 1)]);
      }
      if (cancelled) return;
      setServerUnreachable(false);
      setLoading(false);
      setAuthReady(true);
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, [checkSession]);

  const refreshUserData = async (): Promise<boolean> => {
    setLoading(true);
    const result = await fetchUserData();
    setLoading(false);
    return result;
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      await authService.login({
        username: email,
        password
      });

      // /login only returns an access token, no profile fields - fetch the
      // full business profile right away so the header (name, avatar
      // initials) is correct immediately, without requiring a page reload.
      // A transient failure here must not fail the login itself.
      await fetchUserData();
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (
    businessName: string, 
    email: string, 
    phoneNumber: string, 
    password: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      await authService.register({
        business_name: businessName,
        email,
        phone_number: phoneNumber,
        password
      });
      
      // After successful registration, user needs to verify OTP
      // We don't log them in automatically
      return true;
    } catch (error) {
      console.error('Signup error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (email: string, otp: string): Promise<boolean> => {
    try {
      setLoading(true);
      await authService.verifyOtp({ email, otp });
      return true;
    } catch (error) {
      console.error('OTP verification error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async (email: string): Promise<boolean> => {
    try {
      setLoading(true);
      await authService.resendOtp({ email });
      return true;
    } catch (error) {
      console.error('Resend OTP error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email: string): Promise<boolean> => {
    try {
      setLoading(true);
      await authService.forgotPassword({ email });
      return true;
    } catch (error) {
      console.error('Forgot password error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (businessId: string, token: string, newPassword: string): Promise<boolean> => {
    try {
      setLoading(true);
      await authService.resetPassword({ 
        business_id: businessId,
        token, 
        new_password: newPassword 
      });
      return true;
    } catch (error) {
      console.error('Reset password error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    login,
    signup,
    verifyOtp,
    resendOtp,
    forgotPassword,
    resetPassword,
    refreshUserData,
    logout,
    loading,
    authReady,
    serverUnreachable
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext; 